<template>
  <div v-if="groupByFamilyStyle">
    <div
      v-for="iconGroup in groups"
      :key="`${iconGroup.family}-${iconGroup.style}`"
    >
      <h3 class="h5 text-uppercase margin-y-2xl" style="letter-spacing: var(--letter-spacing-2xl); --headings-font-weight: var(--font-weight-black)">
        {{ sectionHeader(iconGroup.family, iconGroup.style) }}
      </h3>
      <div :class="groupCssClasses">
        <icon
          v-for="icon in iconGroup.icons.slice(0, maxIcons)"
          :key="icon.objectID"
          :icon="icon"
          :icon-style="icon.style"
          :selected-icon-id="selectedIconId"
          :selected-family="selectedFamily"
          :selected-style="selectedStyle"
          :is-pro-user="isProUser"
          :results-view="resultsView"
          :version="version"
          @toggleIcon="toggleIcon"
        />

        <view-more
          v-if="iconGroup.icons.length >= maxIcons"
          :selected-family="iconGroup.family"
          :selected-style="iconGroup.style"
          :has-search-term="hasSearchTerm"
          @viewMore="$emit('viewMore', $event)"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    :class="groupCssClasses"
  >
    <icon
      v-for="icon in icons"
      :key="icon.objectID"
      :icon="icon"
      :icon-style="icon.style"
      :selected-icon-id="selectedIconId"
      :selected-family="selectedFamily"
      :selected-style="selectedStyle"
      :is-pro-user="isProUser"
      :results-view="resultsView"
      :version="version"
      @toggleIcon="toggleIcon"
    />
  </div>
</template>

<script>
  import { createWidgetMixin } from 'vue-instantsearch'
  import { connectHits } from 'instantsearch.js/es/connectors'
  import startCase from 'lodash/startCase'
  import Icon from 'app/views/icons/components/icons/Icon'
  import ViewMore from './ViewMore'
  import { isEqual } from 'lodash'

  const MAX_ICONS = 16 // determines how many icons are displayed before the "View More" button

  export default {
    name: 'SearchRefinementWidget',

    components: {
      Icon,
      ViewMore
    },

    mixins: [createWidgetMixin({ connector: connectHits })],

    props: {
      resultsView: {
        type: String,
        required: true
      },
      isProUser: {
        type: Boolean
      },
      selectedIconId: {
        type: String
      },
      selectedFamily: {
        type: String
      },
      selectedStyle: {
        type: String
      },
      version: {
        type: Number,
        default: null
      },
      searchFieldFocus: {
        type: Boolean,
        required: true
      },
      hasSearchTerm: {
        type: Boolean
      },
      groupByFamilyStyle: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        iconObjectId: null,
        groupIndex: 0,
        groupData: [],
        maxIcons: MAX_ICONS
      }
    },

    computed: {
      groupCssClasses() {
        return [this.resultsView, 'icon-listing margin-top-lg margin-bottom-4xl']
      },

      collectionType() {
        return this.$route.query['ic']
      },

      visualTags() {
        return this.getQueryStringArray('it')
      },

      styles() {
        return this.getQueryStringArray('s')
      },

      iconPacks() {
        return this.getQueryStringArray('ip')
      },

      groups() {
        return this.groupData
      },

      icons() {
        return this.state ? this.state.hits : null
      }
    },

    watch: {
      state(newState, oldState) {
        // Algolia state changes frequently, even when the hits don't change
        // This prevents multiple calls to buildGroups when the state changes

        if (!isEqual(newState, oldState)) {
          this.buildGroups()
        }
      }
    },
    created() {
      document.addEventListener('keydown', this.keydownEvent)
    },

    destroyed() {
      document.removeEventListener('keydown', this.keydownEvent)
    },

    methods: {
      buildGroups() {
        // We manually group icons by taking the current hits and copying icons into groups based on selected icon packs and styles
        // This relies on the icon_packs_list and family_styles properties of each icon
        if (this.state && this.state.hits) {
          const freeCollection = this.collectionType === 'free'

          const results = this.state.hits.reduce((acc, icon) => {
            const filteredPacks = this.filterIconPacks(icon.icon_packs_list)

            filteredPacks.forEach((iconPack) => {
              const filteredStyles = this.filterFamilyStyles(iconPack.family_styles)

              filteredStyles.forEach((fs) => {
                const group = acc.find((i) => i.family === fs.family && i.style === fs.style)
                const iconCopy = this.copyIcon(icon, fs.family, fs.style)

                if (freeCollection) {
                  if (iconCopy.is_free) {
                    this.addIcon(iconCopy, acc, fs, group)
                  }
                } else {
                  this.addIcon(iconCopy, acc, fs, group)
                }
              })
            })

            return acc
          }, [])

          this.groupData = results
        } else {
          this.groupData = []
        }
      },

      addIcon(icon, acc, fs, group) {
        // Add icon to group if it exists, otherwise create a new group
        if (group) {
          const found = group.icons.find((i) => i.objectID === icon.objectID)
          if (!found) {
            group.icons.push(icon)
          }
        } else {
          acc.push({
            family: fs.family,
            style: fs.style,
            icons: [icon]
          })
        }
      },

      getQueryStringArray(queryString) {
        const query = this.$route.query[queryString]
        return query ? query.split(',') : []
      },

      copyIcon(icon, family, style) {
        const iconCopy = structuredClone(icon)
        iconCopy.objectID = `i:${family}:${style}:${icon.name}`
        iconCopy.family = family
        iconCopy.style = style
        iconCopy.is_free = this.isFree(icon, family, style)
        return iconCopy
      },

      filterIconPacks(iconPacks) {
        return iconPacks.filter((iconPack) => {
          const matchesBrandsCollection = this.collectionType === 'brands' ? iconPack.pack === 'brands' : true
          const matchesSelectedIconPacks = this.iconPacks.length > 0 ? this.iconPacks.includes(iconPack.pack) : true

          return matchesBrandsCollection && matchesSelectedIconPacks
        })
      },

      filterFamilyStyles(familyStyles) {
        return familyStyles.filter((fs) => {
          const visualTags = fs['visual-tags'] ? fs['visual-tags'] : []
          const matchesVisualTags = this.visualTags.length > 0 ? visualTags.some((value) => this.visualTags.includes(value)) : true
          const matchesStyle = this.styles.length > 0 ? this.styles.includes(fs.style) : true

          return matchesVisualTags && matchesStyle
        })
      },

      isFree(icon, family, style) {
        return icon.membership['free'].filter((fs) => fs.family === family && fs.style === style).length > 0
      },

      keydownEvent(event) {
        if (event.isComposing) {
          return
        }

        if (!this.searchFieldFocus) {
          if (event.code == 'ArrowRight') {
            this.next()
          }

          if (event.code == 'ArrowLeft') {
            this.previous()
          }

          if (event.code == 'Escape') {
            this.closeIcon()
          }
        }
      },

      toggleIcon(icon, elementTag, objectId) {
        this.state.sendEvent('click', icon, 'Icon Clicked')
        this.iconObjectId = objectId
        this.$emit('toggleIcon', icon, elementTag)
      },

      closeIcon() {
        this.$emit('closeIcon')
      },

      next() {
        if (this.state) {
          let nextIconIndex = this.getCurrentIconIndex() + 1

          if (this.hasNextIcon(nextIconIndex)) {
            const nextIcon = this.getNextIcon()
            this.iconObjectId = nextIcon.objectID
            this.$emit('toggleIcon', nextIcon, `icon-${nextIcon.family}-${nextIcon.name}-${nextIcon.style}`)
            this.scrollToIcon(`icon-${nextIcon.family}-${nextIcon.name}-${nextIcon.style}`)
          }
        }
      },

      hasNextIcon(nextIconIndex) {
        if (this.groupByFamilyStyle) {
          return nextIconIndex <= this.getMaxIndex() || this.groupIndex !== this.groups.length - 1
        } else {
          return nextIconIndex < this.state.hits.length
        }
      },

      getNextIcon() {
        let nextIconIndex = this.getCurrentIconIndex() + 1

        if (this.groupByFamilyStyle) {
          if (nextIconIndex >= this.groups[this.groupIndex].icons.length || nextIconIndex >= MAX_ICONS) {
            this.groupIndex = this.groupIndex + 1
            nextIconIndex = 0
          }

          return this.groups[this.groupIndex].icons[nextIconIndex]
        } else {
          return this.state.hits[nextIconIndex]
        }
      },

      previous() {
        if (this.state) {
          const previousIconIndex = this.getCurrentIconIndex() - 1

          if (previousIconIndex >= 0 || this.groupIndex !== 0) {
            const previousIcon = this.getPreviousIcon()
            this.iconObjectId = previousIcon.objectID
            this.$emit('toggleIcon', previousIcon, `icon-${previousIcon.family}-${previousIcon.name}-${previousIcon.style}`)
            this.scrollToIcon(`icon-${previousIcon.family}-${previousIcon.name}-${previousIcon.style}`)
          }
        }
      },

      getPreviousIcon() {
        let previousIconIndex = this.getCurrentIconIndex() - 1

        if (this.groupByFamilyStyle) {
          if (previousIconIndex < 0) {
            this.groupIndex = this.groupIndex === 0 ? this.groups.length : this.groupIndex - 1
            previousIconIndex = this.getMaxIndex()
          }

          return this.groups[this.groupIndex].icons[previousIconIndex]
        } else {
          return this.state.hits[previousIconIndex]
        }
      },

      getMaxIndex() {
        const iconCount = this.groups[this.groupIndex].icons.length
        return iconCount < MAX_ICONS ? iconCount - 1 : MAX_ICONS - 1
      },

      getCurrentIconIndex() {
        if (this.groupByFamilyStyle) {
          return this.getGroupIconIndex()
        } else {
          return this.state ? this.state.hits.findIndex((i) => i.objectID == this.iconObjectId) : 0
        }
      },

      getGroupIconIndex() {
        // objectID format: i:family:style:icon-name
        const objectParts = this.iconObjectId.split(':')
        const family = objectParts[1]
        const style = objectParts[2]

        this.groupIndex = this.groups.findIndex((i) => i.family === family && i.style === style)
        return this.groups[this.groupIndex].icons.findIndex((i) => i.objectID == this.iconObjectId)
      },

      scrollToIcon(iconElement) {
        const element = document.querySelector(`#${iconElement}`)
        element.scrollIntoView()
      },

      sectionHeader(family, style) {
        return `${startCase(family)} ${startCase(style)}`
      }
    }
  }
</script>
